import { parseData } from '@devhacker/shared/utils/parseData';
import { filterWidgetsByVisibilityWidgetsCondition } from '~/utils/widgets';
import { sidebarSchema } from '~/schemas/widgetsSchema';
import type { TContent, TVisibilityWidgetsCondition } from '~/types/common';
import { wrapPiniaStore } from '@devhacker/shared/utils/wrapPiniaStore';
import { defineStore } from 'pinia';
import { cacheApiResponseWithRedis } from '@devhacker/shared/utils/cacheApiResponseWithRedis';

export const useWidgetsStore = wrapPiniaStore(
  defineStore('widgets', {
    state: () => ({
      sidebar: [] as TContent[],
      home: [] as TContent[],
      'above-menu': [] as TContent[],
      'below-menu': [] as TContent[],
      'above-comments': [] as TContent[],
      'below-comments': [] as TContent[],
      'after-first-paragraph': [] as TContent[],
      'post-dynamic': [] as TContent[],
      scripts: [] as TContent[],
      visibilityWidgetsCondition: [] as TVisibilityWidgetsCondition[],
    }),

    getters: {
      sidebarWidgets: (state) => {
        return (
          state.sidebar?.filter(
            filterWidgetsByVisibilityWidgetsCondition(state.visibilityWidgetsCondition),
          ) || []
        );
      },
      homeWidgets: (state) => {
        return (
          state.home?.filter(
            filterWidgetsByVisibilityWidgetsCondition(state.visibilityWidgetsCondition),
          ) || []
        );
      },
      aboveMenuWidgets: (state) => {
        return (
          state['above-menu']?.filter(
            filterWidgetsByVisibilityWidgetsCondition(state.visibilityWidgetsCondition),
          ) || []
        );
      },
      belowMenuWidgets: (state) => {
        return (
          state['below-menu']?.filter(
            filterWidgetsByVisibilityWidgetsCondition(state.visibilityWidgetsCondition),
          ) || []
        );
      },
      aboveCommentsWidgets: (state) => {
        return (
          state['above-comments']?.filter(
            filterWidgetsByVisibilityWidgetsCondition(state.visibilityWidgetsCondition),
          ) || []
        );
      },
      belowCommentsWidgets: (state) => {
        return (
          state['below-comments']?.filter(
            filterWidgetsByVisibilityWidgetsCondition(state.visibilityWidgetsCondition),
          ) || []
        );
      },
      afterFirstParagraphWidgets: (state) => {
        return (
          state['after-first-paragraph']?.filter(
            filterWidgetsByVisibilityWidgetsCondition(state.visibilityWidgetsCondition),
          ) || []
        );
      },
      postDynamic: (state) => {
        return (
          state['post-dynamic']?.filter(
            filterWidgetsByVisibilityWidgetsCondition(state.visibilityWidgetsCondition),
          ) || []
        );
      },
      scriptsWidgets: (state) => {
        return (
          state.scripts?.filter(
            filterWidgetsByVisibilityWidgetsCondition(state.visibilityWidgetsCondition),
          ) || []
        );
      },
    },

    actions: {
      async fetchWidgets() {
        const { $api, $redisClient } = await useNuxtApp();

        const { data } = await cacheApiResponseWithRedis(
          'FETCH_WIDGETS',
          $api.widgets.sidebars,
          $redisClient,
          60 * 5, // 5 минут, потому что в сайдбаре приходят комментарии
        );

        const parsedResponse = parseData(data, sidebarSchema);

        this.sidebar = parsedResponse.sidebar || [];
        this.home = parsedResponse.home || [];
        this['above-menu'] = parsedResponse['above-menu'] || [];
        this['below-menu'] = parsedResponse['below-menu'] || [];
        this['above-comments'] = parsedResponse['above-comments'] || [];
        this['below-comments'] = parsedResponse['below-comments'] || [];
        this['after-first-paragraph'] = parsedResponse['after-first-paragraph'] || [];
        this['post-dynamic'] = parsedResponse['post-dynamic'] || [];
        this.scripts = parsedResponse.scripts || [];
      },

      setVisibilityWidgetsCondition(conditions: TVisibilityWidgetsCondition[]) {
        this.visibilityWidgetsCondition = conditions;
      },
    },
  }),
);
